import { Grid } from "@mui/material";
import { useMember } from "api/members";
import { useTransactions } from "api/transactions";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { StatusValue } from "components/Table/StatusValue";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListResponse } from "types/listing";
import { links } from "utilities/links";
import { KeyValuePair } from "utilities/objects";
import { MatchTransactionModal } from "../MatchTransactionModal";
import { CustomAttributesModal } from "./CustomAttributesModal";
import { CustomerDetailsSection } from "./CustomerDetailsSection";
import { EarnedRewardsSection } from "./EarnedRewardsSection";
import { EarnedUnitsSection } from "./EarnedUnitsSection";
import { MatchedMemberSection } from "./MatchedMemberSection";
import { ProductsSection } from "./ProductsSection";
import { RelatedTransactions } from "./RelatedTransactions";
import { TransactionDetailsSection } from "./TransactionDetailsSection";
import { TriggeredAchievements } from "./TriggeredAchievements";

export enum Modals {
  MatchTransaction,
  CustomAttributes,
}

export function SingleTransaction() {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { id: transactionID } = useParams<{ id: string }>();
  const { assignTransaction, useTransactionData } = useTransactions();
  const { data: transactionData } = useTransactionData(transactionID as string);
  const isMatchedTransaction = transactionData?.matched;
  const transactionDocumentNumber = transactionData?.header?.documentNumber;
  const transactionDocumentType = transactionData?.header?.documentType;

  const [open, setOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState<{
    transaction: string;
    mode: number;
  }>();
  const [customAttributes, setCustomAttributes] = useState<KeyValuePair[]>();

  const { useMembersList } = useMember();
  const [searchedMemberValue, setSearchedMemberValue] = useState("");
  const { data: searchedMembers } = useMembersList({
    filters: searchedMemberValue
      ? [
          {
            condition: "",
            query: "identifier",
            value: searchedMemberValue,
          },
        ]
      : [],
  }) as {
    data: ListResponse;
  };

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "transactions" }),
      path: links.transactionsList,
    },
  };

  const documentType = transactionData?.header?.documentType;
  const isSellTransaction = transactionDocumentType === "sell";

  const [activeTab, setActiveTab] = useState(0);

  const items = [
    { id: 0, name: "all" },
    {
      component: <MatchedMemberSection transactionData={transactionData} />,
      id: 1,
      name: "matchedMember",
    },
    {
      component: <EarnedUnitsSection transactionId={transactionID} />,
      id: 2,
      name: "earnedUnits",
    },
    {
      component: (
        <ProductsSection
          transactionData={transactionData}
          openCustomAttributesModal={() => {
            setSelectedModal({
              transaction: `${transactionDocumentNumber}`,
              mode: Modals.CustomAttributes,
            });
            setOpen(true);
          }}
          setCustomAttributes={setCustomAttributes}
        />
      ),
      id: 3,
      name: "products",
    },
    {
      component: <EarnedRewardsSection transactionId={transactionID} />,
      id: 4,
      name: "earnedRewards",
    },
    {
      id: 5,
      component: (
        <TriggeredAchievements
          memberId={transactionData?.customerId || ""}
          transactionId={transactionID}
        />
      ),
      name: "triggeredAchievements",
    },
    ...[
      transactionData && transactionData.header
        ? {
            id: 6,
            component: (
              <RelatedTransactions
                type={documentType as "sell" | "return"}
                documentNumber={
                  documentType === "sell"
                    ? (transactionData.header.documentNumber as string)
                    : (transactionData.header.linkedDocumentNumber as string)
                }
              />
            ),
            name: "relatedTransactions",
          }
        : {},
    ],
  ].filter((value) => Object.keys(value).length !== 0);

  const filteredItems =
    activeTab === 0 ? items : items.filter((item) => item.id === activeTab);

  return (
    <>
      <TopHeader
        title={(transactionID && transactionID) || ""}
        clearLink={links.transactionsList}
        breadcrumbs={breadcrumbs}
        badge={
          transactionDocumentType && (
            <StatusValue
              status={transactionDocumentType === "sell" || "warning"}
              label={message({
                id: isSellTransaction ? "purchase" : "return",
              })}
            />
          )
        }
      />

      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            disabled={!transactionData || isMatchedTransaction}
            menuItems={[
              {
                text: message({ id: "editTransaction" }),
                onClick: () =>
                  navigate(links.editTransaction(transactionID as string)),
              },
              {
                text: message({ id: "matchWithMember" }),
                onClick: () => {
                  setOpen(true);
                  setSelectedModal({
                    transaction: `${transactionDocumentNumber}`,
                    mode: Modals.MatchTransaction,
                  });
                },
              },
            ]}
            data-test-id="transaction"
          />
        </SubheaderWrapper>
      </Subheader>

      <Grid container columnSpacing={12} rowSpacing={16} mt={-8} pb={24}>
        <TransactionDetailsSection
          transactionData={transactionData}
          openCustomAttributesModal={() => {
            setSelectedModal({
              transaction: `${transactionDocumentNumber}`,
              mode: Modals.CustomAttributes,
            });
            setCustomAttributes(transactionData?.header?.labels);
            setOpen(true);
          }}
        />
        <CustomerDetailsSection transactionData={transactionData} />
        <Grid item xs={12}>
          <OLTabs
            items={items.map((tab) => ({
              id: tab.id?.toString(),
              name: message({ id: tab.name }),
            }))}
            value={activeTab.toString()}
            onChange={(_, item) => {
              setActiveTab(Number(item));
            }}
          />
        </Grid>

        {filteredItems.map((item) => (
          <>
            {item.component && (
              <Grid item xs={12}>
                {item.component}
              </Grid>
            )}
          </>
        ))}
      </Grid>
      {selectedModal?.mode === Modals.MatchTransaction && (
        <MatchTransactionModal
          open={open}
          close={() => setOpen(false)}
          selectedModal={selectedModal}
          assignTransaction={assignTransaction}
          setSearchedMemberValue={setSearchedMemberValue}
          searchedMembers={searchedMembers}
        />
      )}
      {selectedModal?.mode === Modals.CustomAttributes && (
        <CustomAttributesModal
          open={open}
          close={() => {
            setOpen(false);
            setCustomAttributes(undefined);
          }}
          customAttributes={customAttributes}
        />
      )}
    </>
  );
}
